import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Card,
    Button,
    Badge,
    Table,
    Spinner,
    Flex,
    Textarea,
    NativeSelect,
    Dialog,
    Alert,
} from '@chakra-ui/react';
import { FiCheck, FiX, FiClock, FiCheckCircle, FiUser } from 'react-icons/fi';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';

const API_URL = config.BACKEND_URL;

const AgentSuggestions = () => {
    const { user } = useAuth();
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [actionLoading, setActionLoading] = useState(null);
    const [error, setError] = useState(null);

    // Dialog states
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);
    const [dialogType, setDialogType] = useState(null); // 'approve' or 'reject'
    const [approvalNote, setApprovalNote] = useState('');

    const fetchSuggestions = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${API_URL}/api/agent-suggestions?status=${statusFilter}`,
                { withCredentials: true }
            );
            setSuggestions(response.data);
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setError('Failed to load suggestions');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchSuggestions();
    }, [fetchSuggestions]);

    const handleOpenDialog = (suggestion, type) => {
        setSelectedSuggestion(suggestion);
        setDialogType(type);
        setApprovalNote('');
    };

    const handleCloseDialog = () => {
        setSelectedSuggestion(null);
        setDialogType(null);
        setApprovalNote('');
    };

    const handleApprove = async () => {
        if (!selectedSuggestion) return;

        try {
            setActionLoading(selectedSuggestion.id);
            await axios.put(
                `${API_URL}/api/agent-suggestions/${selectedSuggestion.id}/approve`,
                { approval_note: approvalNote },
                { withCredentials: true }
            );

            alert('Suggestion approved successfully!');
            handleCloseDialog();
            fetchSuggestions();
        } catch (error) {
            console.error('Error approving suggestion:', error);
            alert(error.response?.data?.error || 'Failed to approve suggestion');
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async () => {
        if (!selectedSuggestion) return;

        try {
            setActionLoading(selectedSuggestion.id);
            await axios.put(
                `${API_URL}/api/agent-suggestions/${selectedSuggestion.id}/reject`,
                { approval_note: approvalNote },
                { withCredentials: true }
            );

            alert('Suggestion rejected!');
            handleCloseDialog();
            fetchSuggestions();
        } catch (error) {
            console.error('Error rejecting suggestion:', error);
            alert(error.response?.data?.error || 'Failed to reject suggestion');
        } finally {
            setActionLoading(null);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return (
                    <Badge colorPalette="yellow" variant="subtle">
                        <HStack spacing={1}>
                            <FiClock />
                            <Text>Pending</Text>
                        </HStack>
                    </Badge>
                );
            case 'approved':
                return (
                    <Badge colorPalette="green" variant="subtle">
                        <HStack spacing={1}>
                            <FiCheck />
                            <Text>Approved</Text>
                        </HStack>
                    </Badge>
                );
            case 'rejected':
                return (
                    <Badge colorPalette="red" variant="subtle">
                        <HStack spacing={1}>
                            <FiX />
                            <Text>Rejected</Text>
                        </HStack>
                    </Badge>
                );
            default:
                return <Badge>{status}</Badge>;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('en-US', {
            timeZone: 'Asia/Ho_Chi_Minh',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <Container maxW="container.xl" py={6}>
            <VStack spacing={6} align="stretch">
                {/* Header */}
                <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                    <Box>
                        <Heading size="lg" color="purple.600">Agent Suggestions</Heading>
                        <Text color="gray.600" mt={1}>
                            Manage agent suggestions from users
                        </Text>
                    </Box>

                    {/* Filter */}
                    <HStack spacing={3}>
                        <Text fontSize="sm" fontWeight="medium">Status:</Text>
                        <NativeSelect.Root>
                            <NativeSelect.Field
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                size="sm"
                                w="150px"
                            >
                                <option value="all">All</option>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                        </NativeSelect.Root>
                    </HStack>
                </Flex>

                {/* Error Alert */}
                {error && (
                    <Alert.Root status="error" borderRadius="md">
                        <Alert.Indicator />
                        <Alert.Content>
                            <Alert.Description>{error}</Alert.Description>
                        </Alert.Content>
                    </Alert.Root>
                )}

                {/* Content */}
                <Card.Root>
                    <Card.Body p={0}>
                        {loading ? (
                            <Flex justify="center" align="center" minH="200px">
                                <Spinner size="lg" color="purple.500" />
                            </Flex>
                        ) : suggestions.length === 0 ? (
                            <Flex justify="center" align="center" minH="200px" direction="column" gap={2}>
                                <Box as={FiUser} fontSize="3xl" color="gray.400" />
                                <Text color="gray.500">No suggestions found</Text>
                            </Flex>
                        ) : (
                            <Table.Root size="sm">
                                <Table.Header>
                                    <Table.Row bg="gray.50">
                                        <Table.ColumnHeader>Agent Name</Table.ColumnHeader>
                                        <Table.ColumnHeader>Email</Table.ColumnHeader>
                                        <Table.ColumnHeader>Suggested By</Table.ColumnHeader>
                                        <Table.ColumnHeader>Date</Table.ColumnHeader>
                                        <Table.ColumnHeader>Status</Table.ColumnHeader>
                                        <Table.ColumnHeader>Created</Table.ColumnHeader>
                                        <Table.ColumnHeader textAlign="center">Actions</Table.ColumnHeader>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {suggestions.map((suggestion) => (
                                        <Table.Row key={suggestion.id} _hover={{ bg: 'gray.50' }}>
                                            <Table.Cell fontWeight="medium">{suggestion.suggested_name}</Table.Cell>
                                            <Table.Cell>{suggestion.suggested_email}</Table.Cell>
                                            <Table.Cell>
                                                <VStack align="start" spacing={0}>
                                                    <Text fontSize="sm">{suggestion.suggested_by_name}</Text>
                                                    <Text fontSize="xs" color="gray.500">{suggestion.suggested_by_email}</Text>
                                                </VStack>
                                            </Table.Cell>
                                            <Table.Cell fontSize="sm">{formatDate(suggestion.created_at)}</Table.Cell>
                                            <Table.Cell>{getStatusBadge(suggestion.status)}</Table.Cell>
                                            <Table.Cell>
                                                {suggestion.is_created ? (
                                                    <Badge colorPalette="teal" variant="solid">
                                                        <HStack spacing={1}>
                                                            <FiCheckCircle />
                                                            <Text>Created</Text>
                                                        </HStack>
                                                    </Badge>
                                                ) : (
                                                    <Text fontSize="sm" color="gray.400">-</Text>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {suggestion.status === 'pending' && (
                                                    <HStack spacing={2} justify="center">
                                                        <Button
                                                            size="xs"
                                                            colorPalette="green"
                                                            variant="outline"
                                                            onClick={() => handleOpenDialog(suggestion, 'approve')}
                                                            loading={actionLoading === suggestion.id}
                                                        >
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            size="xs"
                                                            colorPalette="red"
                                                            variant="outline"
                                                            onClick={() => handleOpenDialog(suggestion, 'reject')}
                                                            loading={actionLoading === suggestion.id}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </HStack>
                                                )}
                                                {suggestion.status !== 'pending' && suggestion.approval_note && (
                                                    <Text fontSize="xs" color="gray.500" noOfLines={2}>
                                                        Note: {suggestion.approval_note}
                                                    </Text>
                                                )}
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table.Root>
                        )}
                    </Card.Body>
                </Card.Root>
            </VStack>

            {/* Approve/Reject Dialog */}
            {selectedSuggestion && (
                <Dialog.Root open={true} onOpenChange={(e) => !e.open && handleCloseDialog()}>
                    <Dialog.Backdrop bg="blackAlpha.600" />
                    <Dialog.Positioner>
                        <Dialog.Content maxW="md" borderRadius="xl" mx={4} bg="white">
                            <Dialog.Header
                                bg={dialogType === 'approve' ? 'green.50' : 'red.50'}
                                borderTopRadius="xl"
                                borderBottom="1px"
                                borderColor="gray.200"
                                py={4}
                                px={6}
                            >
                                <Flex justify="space-between" align="center" w="100%">
                                    <Text
                                        fontSize="lg"
                                        fontWeight="bold"
                                        color={dialogType === 'approve' ? 'green.700' : 'red.700'}
                                    >
                                        {dialogType === 'approve' ? 'Approve Suggestion' : 'Reject Suggestion'}
                                    </Text>
                                </Flex>
                            </Dialog.Header>
                            <Dialog.CloseTrigger />

                            <Dialog.Body py={6} px={6}>
                                <VStack spacing={4} align="stretch">
                                    <Box p={3} bg="gray.50" borderRadius="md">
                                        <VStack align="start" spacing={1}>
                                            <Text><strong>Name:</strong> {selectedSuggestion.suggested_name}</Text>
                                            <Text><strong>Email:</strong> {selectedSuggestion.suggested_email}</Text>
                                            <Text><strong>Suggested By:</strong> {selectedSuggestion.suggested_by_name}</Text>
                                        </VStack>
                                    </Box>

                                    <Box>
                                        <Text mb={2} fontWeight="medium">Note (optional):</Text>
                                        <Textarea
                                            value={approvalNote}
                                            onChange={(e) => setApprovalNote(e.target.value)}
                                            placeholder="Enter note..."
                                            rows={3}
                                        />
                                    </Box>
                                </VStack>
                            </Dialog.Body>

                            <Dialog.Footer
                                bg="gray.50"
                                borderBottomRadius="xl"
                                borderTop="1px"
                                borderColor="gray.200"
                                px={6}
                                py={4}
                            >
                                <HStack spacing={3} w="100%" justify="flex-end">
                                    <Button variant="outline" onClick={handleCloseDialog}>
                                        Cancel
                                    </Button>
                                    {dialogType === 'approve' ? (
                                        <Button
                                            colorPalette="green"
                                            onClick={handleApprove}
                                            loading={actionLoading === selectedSuggestion?.id}
                                        >
                                            Confirm Approve
                                        </Button>
                                    ) : (
                                        <Button
                                            colorPalette="red"
                                            onClick={handleReject}
                                            loading={actionLoading === selectedSuggestion?.id}
                                        >
                                            Confirm Reject
                                        </Button>
                                    )}
                                </HStack>
                            </Dialog.Footer>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Dialog.Root>
            )}
        </Container>
    );
};

export default AgentSuggestions;
